import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const roleClass = { admin: "bg-slate-900 text-white", therapist: "bg-indigo-50 text-indigo-700", client: "bg-emerald-50 text-emerald-700", user: "bg-amber-50 text-amber-700" };

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}${u.role === "therapist" ? " and all of their clients" : ""}? This removes all of their progress and practice data.`)) return;
    try { const r = await api.delete(`/admin/users/${u.id}`); toast.success(`Deleted ${r.data.deleted} account${r.data.deleted === 1 ? "" : "s"}`); load(); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14" data-testid="users-admin-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-700 mb-6" data-testid="users-back-link"><ArrowLeft className="w-4 h-4" /> Back to tutorials</Link>
      <p className="eyebrow">Admin panel</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-2 mb-2">Accounts</h1>
      <p className="text-slate-600 mb-8">Every account in the app. Deleting a clinician also removes the clients they invited.</p>
      {!users ? <Skeleton className="h-64 rounded-3xl" /> : (
        <ul className="rounded-3xl bg-white border border-slate-200 divide-y divide-slate-100" data-testid="users-list">
          {users.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center gap-3 px-6 py-4" data-testid={`user-row-${u.id}`}>
              <div className="flex-1 min-w-[200px]">
                <p className="font-medium text-slate-900">{u.name || "—"} <span className="text-slate-500 font-normal">· {u.email}</span></p>
                <p className="text-xs text-slate-500 mt-0.5">
                  <span className={`px-2 py-0.5 rounded-full font-semibold ${roleClass[u.role] || roleClass.user}`}>{u.role === "user" ? "legacy user" : u.role}</span>
                  {" · "}{u.status || "active"}{u.subscription?.plan ? ` · ${u.subscription.plan} (${u.subscription.status})` : ""}{u.license ? ` · ${u.license.type} ${u.license.state} #${u.license.number}` : ""}
                </p>
              </div>
              {u.id !== me?.id && <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => remove(u)} aria-label="Delete account" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
